import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { ETenderStatus, ETenderType, ITender } from 'types/tender'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { formatTimeStampToDate } from 'hooks/date/formattedTime'
import {
  tenderApi,
  useCancelTenderMutation,
  useModifyTenderMutation,
  usePublishTenderMutation,
} from 'services/tender'
import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'
import ConfirmationDialog from 'components/ConfirmationDialog'
import { useBoolean } from 'hooks/useBoolean'
import { EOrganizationType, IUser } from 'types/users'
import TenderCancelReasonDialog from './tender-cancel-reason-dialog'
import { useState } from 'react'
import { UseFormSetValue } from 'react-hook-form'

type Props = {
  tender: ITender
  onSaveTender: () => Promise<void>
  disabled?: boolean
  currentUser: IUser
  setValue: UseFormSetValue<ITender>
}

export const TenderStickyHeader = ({
  tender,
  onSaveTender,
  disabled,
  currentUser,
  setValue,
}: Props) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dialogPublishTender = useBoolean(false)
  const dialogTenderCancel = useBoolean(false)
  const dialogProvideChangeReason = useBoolean(false)
  const [params] = useSearchParams()
  const [changeReason, setChangeReason] = useState('')
  const type = params.get('type') as ETenderType

  const [cancelTender, { isLoading: isCancelLoading }] = useCancelTenderMutation()
  const [publishTender, { isLoading: isPublishLoading }] = usePublishTenderMutation()
  const [modifyTender, { isLoading: isModifyLoading }] = useModifyTenderMutation()

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const handleSaveTender = () => {
    if (changeReason) {
      setValue('changeReason', changeReason)
    }

    onSaveTender().then(() => {
      dispatch(tenderApi.util.invalidateTags(['TenderById']))
      navigate(`/portal/tenders?type=${type}`)
    })
  }

  const handleCorrectClick = () => {
    if (tender.shouldProvideChangeReason) {
      dialogProvideChangeReason.setTrue()
    } else {
      handleSaveTender()
    }
  }

  const handlePublishTender = () => {
    publishTender({ id: tender.id }).then((res) => {
      if (!('error' in res)) {
        dispatch(tenderApi.util.invalidateTags(['TenderById']))
        navigate(`/portal/tenders?type=${type}`)
      } else {
        toast.error(t('failedToCancel'))
      }
    })
  }

  const handleModifyTender = () => {
    modifyTender({ id: tender.id, changeReason }).then((res) => {
      if (!('error' in res)) {
        dispatch(tenderApi.util.invalidateTags(['TenderById']))
        navigate(`/portal/tenders?type=${type}`)
      } else {
        toast.error(t('failedToCancel'))
      }
    })
  }

  const handlePublishConfirm = () => {
    if (tender.shouldProvideChangeReason) {
      dialogProvideChangeReason.setTrue()
    } else {
      handleModifyTender()
    }
  }

  const handleBack = () => navigate(-1)

  const handleCancelTender = (cancelReason: string) => {
    cancelTender({ id: tender.id, cancelReason }).then((res) => {
      if (!('error' in res)) {
        handleBack()
      } else {
        toast.error(t('failedToCancel'))
      }
    })
  }

  const handleChangeReasonClose = () => {
    setChangeReason('')
    dialogProvideChangeReason.setFalse()
  }

  return (
    <Box
      bgcolor="white"
      borderRadius={2}
      position="sticky"
      top={-16}
      mr={-1}
      ml={-1}
      zIndex={2}
    >
      <ConfirmationDialog
        open={dialogPublishTender.isTrue}
        onClose={dialogPublishTender.setFalse}
        onConfirm={handlePublishConfirm}
        text={t('are-you-sure-you-want-to-publish')}
      />
      <TenderCancelReasonDialog
        open={dialogTenderCancel.isTrue}
        onClose={dialogTenderCancel.setFalse}
        onConfirm={handleCancelTender}
        title={t('reject-reason')}
      />
      <Box
        pb={1.5}
        bgcolor={`${theme.palette.primary.main}22`}
        px={1.5}
        borderRadius={2}
        boxShadow="0 0 20px 2px #c0baba;"
      >
        <Grid container spacing={1.5}>
          <Grid alignItems="center" item xs={3}>
            <Typography>
              {t(`${tender.method}_title`)} - {tender.name}
            </Typography>
          </Grid>
          <Grid alignItems="center" item xs={3}>
            {!!tender.startTime && (
              <Typography>
                {t('startDate')}:{' '}
                {tender.startTime && formatTimeStampToDate(tender.startTime)}
              </Typography>
            )}
          </Grid>
          <Grid alignItems="center" item xs={2}>
            <Typography>{t(`tender-status-${tender.status}`)}</Typography>
          </Grid>
          <Grid alignItems="center" item xs={4}>
            <Box display="flex" justifyContent="flex-end">
              {!disabled &&
                (tender.status === ETenderStatus.PUBLISHED ||
                  tender.status === ETenderStatus.MODIFIED) && (
                  <Button
                    onClick={handleCorrectClick}
                    size="small"
                    sx={{ mr: 1.5 }}
                    variant="contained"
                    style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                    disabled={isCancelLoading || isPublishLoading || isModifyLoading}
                  >
                    {t('correct')}
                  </Button>
                )}
              {(tender.status === ETenderStatus.CREATED ||
                tender.status === ETenderStatus.PLANNING) && (
                <>
                  <Button
                    onClick={handleSaveTender}
                    size="small"
                    sx={{ mr: 1.5 }}
                    variant="contained"
                    style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                    disabled={isCancelLoading || isPublishLoading || isModifyLoading}
                  >
                    {t('save')}
                  </Button>
                  <Button
                    onClick={handlePublishTender}
                    size="small"
                    sx={{ mr: 1.5 }}
                    variant="contained"
                    style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                    disabled={isCancelLoading || isPublishLoading || isModifyLoading}
                  >
                    {t('publish')}
                  </Button>
                </>
              )}

              {currentUser.organizationType ===
                EOrganizationType.TENDER_AUTHORITY && (
                <Button
                  color="error"
                  disabled={isCancelLoading || isPublishLoading || isModifyLoading}
                  onClick={dialogTenderCancel.setTrue}
                  size="small"
                  variant="outlined"
                  sx={{ mr: 1.5 }}
                >
                  {t('cancel')}
                </Button>
              )}
              <Button
                sx={{ mr: 1.5 }}
                disabled={isCancelLoading || isPublishLoading || isModifyLoading}
                onClick={handleBack}
                size="small"
                variant="outlined"
              >
                {t('back')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={handleChangeReasonClose}
        open={dialogProvideChangeReason.isTrue}
      >
        <DialogTitle>{t('change-reason')}</DialogTitle>

        <DialogContent>
          <Box sx={{ py: 1 }}>
            <TextField
              fullWidth
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              label={t('provide-change-reason')}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleChangeReasonClose}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            disabled={!changeReason}
            onClick={() => {
              handleChangeReasonClose()
              handleModifyTender()
            }}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
